import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { accountsAPI, transferAPI } from '../services/api';
import transferPinAPI from '../services/transferPinAPI';
import { useNotification } from '../contexts/NotificationContext';
import { handleApiError, formatTransferSuccess, getLoadingMessage } from '../utils/errorHandler';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currencyFormatter';
import SearchableDropdown from '../components/SearchableDropdown';
import TransferPinModal from '../components/TransferPinModal';

// Move transferTypes outside component to prevent re-creation on every render
const Transfer = () => {
  const navigate = useNavigate();
  const notification = useNotification();
  const { user } = useAuth();
  const [selectedTransferType, setSelectedTransferType] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [transferLimits, setTransferLimits] = useState(null);
  const [formData, setFormData] = useState({
    fromAccount: '',
    toAccount: '',
    recipientEmail: '',
    recipientPhone: '',
    amount: '',
    memo: '',
    transferDate: 'today',
    // External transfer fields
    externalBankName: '',
    externalAccountNumber: '',
    externalRoutingNumber: '',
    recipientName: '',
    recipientAddress: '',
    // SEPA transfer fields
    recipientIban: '',
    recipientSwiftCode: '',
    recipientBankName: '',
    recipientCountry: '',
    purposeOfPayment: ''
  });
  const [loading, setLoading] = useState(false);
  const [validatingRecipient, setValidatingRecipient] = useState(false);
  const [recipientValidation, setRecipientValidation] = useState(null);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinModalMode, setPinModalMode] = useState('validate');
  const [pinRequired, setPinRequired] = useState(false);
  const [pinStatus, setPinStatus] = useState(null);

  // Currency formatter for user's currency
  const formatCurrencyWithUserCurrency = (amount) => {
    return formatCurrency(amount, user?.currency_code || 'USD');
  };

  // Transfer types with dynamic fees based on user currency
  const transferTypes = useMemo(() => [
    {
      id: 'between-accounts',
      title: 'Between My Accounts',
      description: 'Transfer money between your own accounts',
      icon: '🔄',
      color: 'from-blue-500 to-blue-600',
      time: 'Instant',
      fee: 'Free'
    },
    {
      id: 'same-bank',
      title: 'Same Bank Transfer',
      description: 'Send money to other First Heritage Community Bank customers',
      icon: '👥',
      color: 'from-green-500 to-green-600',
      time: 'Instant',
      fee: 'Free'
    },
    {
      id: 'external-bank',
      title: 'External Bank Transfer',
      description: 'Transfer to accounts at other banks',
      icon: '🏦',
      color: 'from-purple-500 to-purple-600',
      time: '1-3 business days',
      fee: formatCurrencyWithUserCurrency(3.00)
    },
    {
      id: 'sepa',
      title: 'SEPA Transfer',
      description: 'Send money within Europe (EUR only)',
      icon: '🇪🇺',
      color: 'from-emerald-500 to-emerald-600',
      time: 'Next business day',
      fee: formatCurrencyWithUserCurrency(15.00)
    }
  ], [user?.currency_code]);

  useEffect(() => {
    fetchInitialData();
    checkPinStatus();
  }, []);

  const checkPinStatus = async () => {
    try {
      const response = await transferPinAPI.checkPinRequired();
      console.log('PIN Status Response:', response);
      setPinRequired(response.data.pin_required);
      setPinStatus(response.data);
      console.log('PIN Required set to:', response.data.pin_required);
    } catch (error) {
      console.error('Error checking PIN status:', error);
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [accountsRes, recentTransfersRes, limitsRes] = await Promise.all([
        accountsAPI.getAccounts(),
        transferAPI.getRecentTransfers(),
        transferAPI.getLimits()
      ]);

      if (accountsRes.status === 'success') {
        setAccounts(accountsRes.data.accounts);
      }
      
      if (recentTransfersRes.status === 'success') {
        setRecentTransfers(recentTransfersRes.data.transfers);
      }
      
      if (limitsRes.status === 'success') {
        setTransferLimits(limitsRes.data);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      handleApiError(error, notification, 'Failed to load transfer data. Please refresh the page and try again.');
      setErrors({ general: 'Failed to load transfer data. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Memoize validateRecipient to prevent unnecessary re-renders
  const validateRecipient = useCallback(async (emailOrPhone) => {
    if (!emailOrPhone || emailOrPhone.length < 3) {
      setRecipientValidation(null);
      return;
    }

    setValidatingRecipient(true);
    try {
      const response = await transferAPI.validateRecipient(emailOrPhone);
      if (response.status === 'success') {
        setRecipientValidation({
          valid: true,
          recipientName: response.data.recipientName,
          recipientId: response.data.recipientId
        });
        setErrors(prev => ({ ...prev, recipient: null }));
        notification.showInfo(`Recipient found: ${response.data.recipientName}`, null, 3000);
      }
    } catch (error) {
      setRecipientValidation({
        valid: false,
        message: error.response?.data?.message || 'Recipient not found'
      });
      setErrors(prev => ({ ...prev, recipient: 'Recipient not found' }));
      handleApiError(error, notification);
    } finally {
      setValidatingRecipient(false);
    }
  }, [notification]);

  // Debounced recipient validation
  useEffect(() => {
    if (selectedTransferType?.id === 'same-bank') {
      const timeout = setTimeout(() => {
        const recipient = formData.recipientEmail || formData.recipientPhone;
        if (recipient) {
          validateRecipient(recipient);
        }
      }, 800);

      return () => clearTimeout(timeout);
    }
  }, [formData.recipientEmail, formData.recipientPhone, selectedTransferType, validateRecipient]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear specific field errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const getAccountBalance = useCallback((accountId) => {
    const account = accounts.find(acc => acc.id === parseInt(accountId));
    return account ? account.balance : 0;
  }, [accounts]);

  const resetForm = () => {
    setFormData({
      fromAccount: '',
      toAccount: '',
      recipientEmail: '',
      recipientPhone: '',
      amount: '',
      memo: '',
      transferDate: 'today',
      externalBankName: '',
      externalAccountNumber: '',
      externalRoutingNumber: '',
      recipientName: '',
      recipientAddress: '',
      recipientIban: '',
      recipientSwiftCode: '',
      recipientBankName: '',
      recipientCountry: '',
      purposeOfPayment: ''
    });
    setErrors({});
    setRecipientValidation(null);
  };

  // Memoize form validation to prevent infinite re-renders
  const isFormValid = useMemo(() => {
    // Basic validation
    if (!formData.fromAccount) return false;
    if (!formData.amount || parseFloat(formData.amount) < 5) return false;
    if (parseFloat(formData.amount) > getAccountBalance(formData.fromAccount)) return false;
    
    // PIN validation - if PIN is required but not set, form is invalid
    if (pinRequired && !pinStatus?.has_pin) return false;

    // Type-specific validation
    switch (selectedTransferType?.id) {
      case 'between-accounts':
        if (!formData.toAccount) return false;
        if (formData.fromAccount === formData.toAccount) return false;
        break;
      
      case 'same-bank':
        if (!formData.recipientEmail && !formData.recipientPhone) return false;
        if (recipientValidation && !recipientValidation.valid) return false;
        break;
      
      case 'external-bank':
        if (!formData.recipientName) return false;
        if (!formData.externalBankName) return false;
        if (!formData.externalAccountNumber) return false;
        if (!formData.externalRoutingNumber) return false;
        if (formData.externalRoutingNumber.length !== 9) return false;
        break;
        
      case 'sepa':
        if (!formData.recipientName) return false;
        if (!formData.recipientIban) return false;
        if (!formData.recipientBankName) return false;
        if (!formData.recipientCountry) return false;
        if (!formData.purposeOfPayment) return false;
        // Basic IBAN validation (starts with 2 letters followed by 2 digits)
        if (!/^[A-Z]{2}[0-9]{2}/.test(formData.recipientIban.toUpperCase())) return false;
        break;
        
      default:
        return false;
    }

    return true;
  }, [
    formData.fromAccount,
    formData.toAccount,
    formData.recipientEmail,
    formData.recipientPhone,
    formData.amount,
    formData.recipientName,
    formData.externalBankName,
    formData.externalAccountNumber,
    formData.externalRoutingNumber,
    selectedTransferType,
    recipientValidation,
    getAccountBalance,
    pinRequired,
    pinStatus?.has_pin
  ]);

  const validateForm = () => {
    const newErrors = {};

    // Basic validation
    if (!formData.fromAccount) {
      newErrors.fromAccount = 'Please select an account to transfer from';
    }

    if (!formData.amount || parseFloat(formData.amount) < 5) {
      newErrors.amount = `Please enter an amount of at least ${formatCurrencyWithUserCurrency(5.00)}`;
    } else if (parseFloat(formData.amount) > getAccountBalance(formData.fromAccount)) {
      newErrors.amount = 'Insufficient funds';
    }

    // Type-specific validation
    switch (selectedTransferType?.id) {
      case 'between-accounts':
        if (!formData.toAccount) {
          newErrors.toAccount = 'Please select an account to transfer to';
        } else if (formData.fromAccount === formData.toAccount) {
          newErrors.toAccount = 'Cannot transfer to the same account';
        }
        break;
      
      case 'same-bank':
        if (!formData.recipientEmail && !formData.recipientPhone) {
          newErrors.recipient = 'Please enter recipient email or phone number';
        } else if (recipientValidation && !recipientValidation.valid) {
          newErrors.recipient = recipientValidation.message;
        }
        break;
      
      case 'external-bank':
        if (!formData.recipientName) {
          newErrors.recipientName = 'Please enter recipient name';
        }
        if (!formData.externalBankName) {
          newErrors.externalBankName = 'Please enter bank name';
        }
        if (!formData.externalAccountNumber) {
          newErrors.externalAccountNumber = 'Please enter account number';
        }
        if (!formData.externalRoutingNumber) {
          newErrors.externalRoutingNumber = 'Please enter routing number';
        } else if (formData.externalRoutingNumber.length !== 9) {
          newErrors.externalRoutingNumber = 'Routing number must be 9 digits';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleReviewTransfer = () => {
    if (validateForm()) {
      setStep(3);
    }
  };

  const handleSubmitTransfer = async (transferPin = null) => {
    setLoading(true);
    setErrors({});

    try {
      let response;
      const transferData = {
        fromAccount: formData.fromAccount,
        amount: parseFloat(formData.amount),
        memo: formData.memo,
        transferDate: formData.transferDate,
        transfer_pin: transferPin
      };

      let transferType;
      let recipientName = null;

      switch (selectedTransferType?.id) {
        case 'between-accounts':
          transferType = 'between-accounts';
          response = await transferAPI.betweenAccounts({
            ...transferData,
            toAccount: formData.toAccount
          });
          break;
        
        case 'same-bank':
          transferType = 'same-bank';
          recipientName = recipientValidation?.recipientName;
          response = await transferAPI.sameBank({
            ...transferData,
            recipientEmail: formData.recipientEmail,
            recipientPhone: formData.recipientPhone,
            recipientId: recipientValidation?.recipientId
          });
          break;
        
        case 'external-bank':
          transferType = 'external-bank';
          recipientName = formData.recipientName;
          response = await transferAPI.externalBank({
            ...transferData,
            recipientName: formData.recipientName,
            bankName: formData.externalBankName,
            accountNumber: formData.externalAccountNumber,
            routingNumber: formData.externalRoutingNumber,
            recipientAddress: formData.recipientAddress
          });
          break;
        
        case 'sepa':
          transferType = 'sepa';
          recipientName = formData.recipientName;
          response = await transferAPI.sepa({
            ...transferData,
            recipientName: formData.recipientName,
            iban: formData.recipientIban,
            swiftCode: formData.recipientSwiftCode,
            bankName: formData.recipientBankName,
            country: formData.recipientCountry,
            purposeOfPayment: formData.purposeOfPayment
          });
          break;
        
        default:
          throw new Error('Invalid transfer type');
      }

      if (response.status === 'success') {
        // Show success notification using utility
        const successData = formatTransferSuccess(transferType, formData.amount, recipientName);
        notification.showSuccess(successData.message, successData.title, 6000);
        
        // Navigate back to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        throw new Error(response.message || 'Transfer failed');
      }
    } catch (error) {
      console.error('Transfer error:', error);
      const parsedError = handleApiError(error, notification);
      setErrors({ general: parsedError.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRepeatTransfer = async (transferId) => {
    try {
      setLoading(true);
      const response = await transferAPI.repeatTransfer(transferId);
      
      if (response.status === 'success') {
        notification.showSuccess('Transfer repeated successfully!', 'Success', 5000);
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      }
    } catch (error) {
      console.error('Repeat transfer error:', error);
      handleApiError(error, notification, 'Failed to repeat transfer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTransferTypeSelect = (type) => {
    setSelectedTransferType(type);
    resetForm();
    setStep(2);
  };

  const handlePinSuccess = (pin) => {
    if (pinModalMode === 'validate') {
      // PIN validated, proceed with transfer
      handleSubmitTransfer(pin);
    } else if (pinModalMode === 'set' || pinModalMode === 'change') {
      // PIN set/changed, refresh status and proceed to review
      checkPinStatus();
      setStep(3);
    }
    setPinModalOpen(false);
  };

  const handlePinModalClose = () => {
    setPinModalOpen(false);
    if (pinModalMode === 'validate') {
      // If PIN validation was cancelled, stay on review step
      // Don't change step, just close modal
    }
  };

  const handleSetPin = () => {
    setPinModalMode('set');
    setPinModalOpen(true);
  };

  const handleChangePin = () => {
    setPinModalMode('change');
    setPinModalOpen(true);
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      setStep(1);
      setSelectedTransferType(null);
    } else {
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">{getLoadingMessage('transfer')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3">
          <div className="flex items-center space-x-3">
            <button 
              onClick={handleBack}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Transfer Money</h1>
              <p className="text-xs text-gray-500">
                {step === 1 ? 'Choose transfer type' : 
                 step === 2 ? selectedTransferType?.title :
                 'Review & Confirm'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-6">
        {step === 1 ? (
          /* Transfer Type Selection */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
              How would you like to transfer money?
            </h2>
            
            <div className="space-y-4">
              {transferTypes.map((type, index) => (
                <motion.button
                  key={type.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  onClick={() => handleTransferTypeSelect(type)}
                  className="w-full bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all text-left"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${type.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <span className="text-white text-2xl">{type.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 mb-1">{type.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">{type.description}</p>
                      <div className="flex items-center space-x-4 text-xs">
                        <span className="text-green-600 font-medium">⏱️ {type.time}</span>
                        <span className="text-blue-600 font-medium">💰 {type.fee}</span>
                      </div>
                    </div>
                    <div className="text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Recent Transfers */}
            <div className="mt-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Transfers</h3>
              {recentTransfers.length > 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="divide-y divide-gray-100">
                    {recentTransfers.map((transfer) => (
                      <div key={transfer.id} className="flex items-center space-x-3 p-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          transfer.type === 'between-accounts' ? 'bg-blue-100' :
                          transfer.type === 'same-bank' ? 'bg-green-100' : 'bg-purple-100'
                        }`}>
                          <span className={`text-lg ${
                            transfer.type === 'between-accounts' ? 'text-blue-600' :
                            transfer.type === 'same-bank' ? 'text-green-600' : 'text-purple-600'
                          }`}>
                            {transfer.type === 'between-accounts' ? '🔄' :
                             transfer.type === 'same-bank' ? '👥' : '🏦'}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">{transfer.description}</p>
                          <p className="text-xs text-gray-500">{transfer.date} • {formatCurrencyWithUserCurrency(transfer.amount)}</p>
                        </div>
                        <button 
                          onClick={() => handleRepeatTransfer(transfer.id)}
                          disabled={loading}
                          className="text-blue-600 text-sm font-medium hover:text-blue-700 disabled:opacity-50"
                        >
                          Repeat
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center">
                  <p className="text-gray-500">No recent transfers</p>
                </div>
              )}
            </div>
          </motion.div>
        ) : step === 2 ? (
          /* Transfer Form */
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className={`w-12 h-12 bg-gradient-to-br ${selectedTransferType?.color} rounded-xl flex items-center justify-center`}>
                  <span className="text-white text-lg">{selectedTransferType?.icon}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedTransferType?.title}</h3>
                  <p className="text-sm text-gray-600">{selectedTransferType?.description}</p>
                </div>
              </div>

              <div className="space-y-5">
                {/* From Account */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Account</label>
                  <SearchableDropdown
                    options={accounts.map(account => ({
                      value: account.id,
                      label: `${account.account_name} • ${formatCurrencyWithUserCurrency(account.balance)}`,
                      account: account
                    }))}
                    value={formData.fromAccount}
                    onChange={(value) => handleInputChange({ target: { name: 'fromAccount', value } })}
                    placeholder="Search and select account..."
                    searchPlaceholder="Search accounts..."
                    getSearchText={(option) => `${option.account.account_name} ${option.account.account_type?.name || ''}`}
                    renderOption={(option) => (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{option.account.account_name}</p>
                          <p className="text-sm text-gray-500">{option.account.account_type?.name || 'Account'}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">{formatCurrencyWithUserCurrency(option.account.balance)}</p>
                          <p className="text-xs text-gray-500">****{option.account.account_number?.slice(-4) || '0000'}</p>
                        </div>
                      </div>
                    )}
                    renderSelected={(option) => (
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{option.account.account_name}</span>
                        <span className="text-gray-600">{formatCurrencyWithUserCurrency(option.account.balance)}</span>
                      </div>
                    )}
                  />
                </div>

                {/* To Account/Recipient */}
                {selectedTransferType?.id === 'between-accounts' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">To Account</label>
                    <SearchableDropdown
                      options={accounts
                        .filter(account => account.id !== parseInt(formData.fromAccount))
                        .map(account => ({
                          value: account.id,
                          label: `${account.account_name} • ${account.account_type?.name || 'Account'}`,
                          account: account
                        }))}
                      value={formData.toAccount}
                      onChange={(value) => handleInputChange({ target: { name: 'toAccount', value } })}
                      placeholder="Search and select destination account..."
                      searchPlaceholder="Search accounts..."
                      getSearchText={(option) => `${option.account.account_name} ${option.account.account_type?.name || ''}`}
                      renderOption={(option) => (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{option.account.account_name}</p>
                            <p className="text-sm text-gray-500">{option.account.account_type?.name || 'Account'}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">{formatCurrencyWithUserCurrency(option.account.balance)}</p>
                            <p className="text-xs text-gray-500">****{option.account.account_number?.slice(-4) || '0000'}</p>
                          </div>
                        </div>
                      )}
                      renderSelected={(option) => (
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{option.account.account_name}</span>
                          <span className="text-gray-600">{option.account.account_type?.name}</span>
                        </div>
                      )}
                      emptyMessage="No available accounts (select a different source account)"
                    />
                  </div>
                )}

                {selectedTransferType?.id === 'same-bank' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Send To</label>
                    <div className="space-y-3">
                      <div className="relative">
                        <input
                          type="email"
                          name="recipientEmail"
                          value={formData.recipientEmail}
                          onChange={handleInputChange}
                          placeholder="Recipient's email address"
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            errors.recipient ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {validatingRecipient && (
                          <div className="absolute right-3 top-3">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-center text-gray-500 text-sm">or</div>
                      
                      <div className="relative">
                        <input
                          type="tel"
                          name="recipientPhone"
                          value={formData.recipientPhone}
                          onChange={handleInputChange}
                          placeholder="Recipient's phone number"
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            errors.recipient ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {validatingRecipient && (
                          <div className="absolute right-3 top-3">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                          </div>
                        )}
                      </div>
                      
                      {recipientValidation && recipientValidation.valid && (
                        <div className="flex items-center space-x-2 text-green-600 text-sm">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Recipient found: {recipientValidation.recipientName}</span>
                        </div>
                      )}
                      
                      {errors.recipient && (
                        <p className="text-sm text-red-600">{errors.recipient}</p>
                      )}
                    </div>
                  </div>
                )}

                {selectedTransferType?.id === 'external-bank' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Name</label>
                      <input
                        type="text"
                        name="recipientName"
                        value={formData.recipientName}
                        onChange={handleInputChange}
                        placeholder="Full name of recipient"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                      <input
                        type="text"
                        name="externalBankName"
                        value={formData.externalBankName}
                        onChange={handleInputChange}
                        placeholder="e.g., Chase Bank"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                        <input
                          type="text"
                          name="externalAccountNumber"
                          value={formData.externalAccountNumber}
                          onChange={handleInputChange}
                          placeholder="Account number"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Routing Number</label>
                        <input
                          type="text"
                          name="externalRoutingNumber"
                          value={formData.externalRoutingNumber}
                          onChange={handleInputChange}
                          placeholder="9-digit routing number"
                          maxLength="9"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {selectedTransferType?.id === 'sepa' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Name</label>
                      <input
                        type="text"
                        name="recipientName"
                        value={formData.recipientName}
                        onChange={handleInputChange}
                        placeholder="Full name of recipient"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">IBAN</label>
                      <input
                        type="text"
                        name="recipientIban"
                        value={formData.recipientIban}
                        onChange={handleInputChange}
                        placeholder="e.g., DE89 3704 0044 0532 0130 00"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                        maxLength="34"
                      />
                      <p className="text-xs text-gray-500 mt-1">International Bank Account Number (up to 34 characters)</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                        <input
                          type="text"
                          name="recipientBankName"
                          value={formData.recipientBankName}
                          onChange={handleInputChange}
                          placeholder="e.g., Deutsche Bank"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">SWIFT/BIC Code (Optional)</label>
                        <input
                          type="text"
                          name="recipientSwiftCode"
                          value={formData.recipientSwiftCode}
                          onChange={handleInputChange}
                          placeholder="e.g., DEUTDEFF"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                          maxLength="11"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                        <SearchableDropdown
                          options={[
                            { value: "AT", label: "Austria", flag: "🇦🇹" },
                            { value: "BE", label: "Belgium", flag: "🇧🇪" },
                            { value: "BG", label: "Bulgaria", flag: "🇧🇬" },
                            { value: "HR", label: "Croatia", flag: "🇭🇷" },
                            { value: "CY", label: "Cyprus", flag: "🇨🇾" },
                            { value: "CZ", label: "Czech Republic", flag: "🇨🇿" },
                            { value: "DK", label: "Denmark", flag: "🇩🇰" },
                            { value: "EE", label: "Estonia", flag: "🇪🇪" },
                            { value: "FI", label: "Finland", flag: "🇫🇮" },
                            { value: "FR", label: "France", flag: "🇫🇷" },
                            { value: "DE", label: "Germany", flag: "🇩🇪" },
                            { value: "GR", label: "Greece", flag: "🇬🇷" },
                            { value: "HU", label: "Hungary", flag: "🇭🇺" },
                            { value: "IS", label: "Iceland", flag: "🇮🇸" },
                            { value: "IE", label: "Ireland", flag: "🇮🇪" },
                            { value: "IT", label: "Italy", flag: "🇮🇹" },
                            { value: "LV", label: "Latvia", flag: "🇱🇻" },
                            { value: "LI", label: "Liechtenstein", flag: "🇱🇮" },
                            { value: "LT", label: "Lithuania", flag: "🇱🇹" },
                            { value: "LU", label: "Luxembourg", flag: "🇱🇺" },
                            { value: "MT", label: "Malta", flag: "🇲🇹" },
                            { value: "MC", label: "Monaco", flag: "🇲🇨" },
                            { value: "NL", label: "Netherlands", flag: "🇳🇱" },
                            { value: "NO", label: "Norway", flag: "🇳🇴" },
                            { value: "PL", label: "Poland", flag: "🇵🇱" },
                            { value: "PT", label: "Portugal", flag: "🇵🇹" },
                            { value: "RO", label: "Romania", flag: "🇷🇴" },
                            { value: "SM", label: "San Marino", flag: "🇸🇲" },
                            { value: "SK", label: "Slovakia", flag: "🇸🇰" },
                            { value: "SI", label: "Slovenia", flag: "🇸🇮" },
                            { value: "ES", label: "Spain", flag: "🇪🇸" },
                            { value: "SE", label: "Sweden", flag: "🇸🇪" },
                            { value: "CH", label: "Switzerland", flag: "🇨🇭" }
                          ]}
                          value={formData.recipientCountry}
                          onChange={(value) => handleInputChange({ target: { name: 'recipientCountry', value } })}
                          placeholder="Search and select country..."
                          searchPlaceholder="Search countries..."
                          renderOption={(option) => (
                            <div className="flex items-center space-x-3">
                              <span className="text-xl">{option.flag}</span>
                              <span className="font-medium">{option.label}</span>
                            </div>
                          )}
                          renderSelected={(option) => (
                            <div className="flex items-center space-x-3">
                              <span className="text-lg">{option.flag}</span>
                              <span className="font-medium">{option.label}</span>
                            </div>
                          )}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Purpose of Payment</label>
                        <select
                          name="purposeOfPayment"
                          value={formData.purposeOfPayment}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select purpose</option>
                          <option value="FAMI">Family support</option>
                          <option value="SALA">Salary payment</option>
                          <option value="TRAD">Trade services</option>
                          <option value="CONS">Construction services</option>
                          <option value="FINC">Financial services</option>
                          <option value="EDUC">Education expenses</option>
                          <option value="MEDI">Medical expenses</option>
                          <option value="OTHR">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                      <div className="flex items-start space-x-2">
                        <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.98-.833-2.75 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-yellow-800">SEPA Transfer Requirements</p>
                          <p className="text-sm text-yellow-700 mt-1">
                            • Additional correspondent bank fees may apply
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500 text-lg">
                      {user?.currency_code === 'USD' ? '$' : 
                       user?.currency_code === 'EUR' ? '€' : 
                       user?.currency_code === 'GBP' ? '£' : 
                       user?.currency_code === 'NGN' ? '₦' : 
                       user?.currency_code === 'GHS' ? '₵' : 
                       user?.currency_code === 'KES' ? 'KSh' : 
                       user?.currency_code === 'ZAR' ? 'R' : 
                       user?.currency_code === 'CAD' ? 'C$' : 
                       user?.currency_code === 'AUD' ? 'A$' : 
                       user?.currency_code === 'JPY' ? '¥' : 
                       user?.currency_code === 'CHF' ? 'CHF' : 
                       user?.currency_code === 'CNY' ? '¥' : 
                       user?.currency_code === 'INR' ? '₹' : 
                       user?.currency_code === 'BRL' ? 'R$' : 
                       user?.currency_code === 'MXN' ? '$' : 
                       user?.currency_code || '$'}
                    </span>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      placeholder="5.00"
                      step="0.01"
                      min="5"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-semibold"
                    />
                  </div>
                  {formData.fromAccount && (
                    <p className="text-sm text-gray-500 mt-1">
                      Available: {formatCurrencyWithUserCurrency(getAccountBalance(formData.fromAccount))}
                    </p>
                  )}
                  {formData.fromAccount && formData.amount && parseFloat(formData.amount) > getAccountBalance(formData.fromAccount) && (
                    <p className="text-sm text-red-600 mt-1">Insufficient funds</p>
                  )}
                </div>

                {/* Memo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What's this for? (Optional)</label>
                  <input
                    type="text"
                    name="memo"
                    value={formData.memo}
                    onChange={handleInputChange}
                    placeholder="Add a note..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* PIN Setup Prompt */}
                {!pinStatus?.has_pin && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.98-.833-2.75 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-yellow-800 mb-1">Transfer PIN Required</h4>
                        <p className="text-sm text-yellow-700 mb-3">
                          You need to set up a 6-digit transfer PIN before you can make transfers. This adds an extra layer of security to your account.
                        </p>
                        <button
                          onClick={handleSetPin}
                          className="text-sm bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors font-medium"
                        >
                          Set Up Transfer PIN
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Transfer Info */}
                <div className="bg-blue-50 rounded-xl p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Processing time:</span>
                    <span className="font-medium text-gray-900">{selectedTransferType?.time}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className="text-gray-600">Transfer fee:</span>
                    <span className="font-medium text-gray-900">{selectedTransferType?.fee}</span>
                  </div>
                </div>

                {/* Error Messages */}
                {errors.general && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-red-600">{errors.general}</p>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <motion.button
                  onClick={handleReviewTransfer}
                  disabled={!isFormValid}
                  whileHover={{ scale: isFormValid ? 1.02 : 1 }}
                  whileTap={{ scale: isFormValid ? 0.98 : 1 }}
                  className={`w-full py-4 rounded-xl font-semibold text-lg transition-colors ${
                    isFormValid
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Review Transfer
                </motion.button>
              </div>
            </div>
          </motion.div>
        ) : (
          /* Review & Confirm */
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className={`w-12 h-12 bg-gradient-to-br ${selectedTransferType?.color} rounded-xl flex items-center justify-center`}>
                  <span className="text-white text-lg">{selectedTransferType?.icon}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Review Transfer</h3>
                  <p className="text-sm text-gray-600">Please confirm the details below</p>
                </div>
              </div>

              {/* Transfer Summary */}
              <div className="space-y-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">Transfer Amount</p>
                    <p className="text-3xl font-bold text-gray-900">{formatCurrencyWithUserCurrency(parseFloat(formData.amount || 0))}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">From:</span>
                    <span className="font-medium text-gray-900">
                      {accounts.find(acc => acc.id === parseInt(formData.fromAccount))?.account_name}
                    </span>
                  </div>

                  {selectedTransferType?.id === 'between-accounts' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">To:</span>
                      <span className="font-medium text-gray-900">
                        {accounts.find(acc => acc.id === parseInt(formData.toAccount))?.account_name}
                      </span>
                    </div>
                  )}

                  {selectedTransferType?.id === 'same-bank' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">To:</span>
                      <span className="font-medium text-gray-900">
                        {formData.recipientEmail || formData.recipientPhone}
                      </span>
                    </div>
                  )}

                  {selectedTransferType?.id === 'external-bank' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">To:</span>
                        <span className="font-medium text-gray-900">{formData.recipientName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bank:</span>
                        <span className="font-medium text-gray-900">{formData.externalBankName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Account:</span>
                        <span className="font-medium text-gray-900">****{formData.externalAccountNumber.slice(-4)}</span>
                      </div>
                    </>
                  )}

                  {selectedTransferType?.id === 'sepa' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">To:</span>
                        <span className="font-medium text-gray-900">{formData.recipientName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">IBAN:</span>
                        <span className="font-medium text-gray-900">{formData.recipientIban}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bank:</span>
                        <span className="font-medium text-gray-900">{formData.recipientBankName}</span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between">
                    <span className="text-gray-600">Processing time:</span>
                    <span className="font-medium text-gray-900">{selectedTransferType?.time}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Fee:</span>
                    <span className="font-medium text-gray-900">{selectedTransferType?.fee}</span>
                  </div>

                  {formData.memo && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Memo:</span>
                      <span className="font-medium text-gray-900">{formData.memo}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* PIN Status */}
              {pinRequired && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <span className="text-sm font-medium text-amber-800">Transfer PIN Required</span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSetPin}
                        className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                      >
                        Set PIN
                      </button>
                      <button
                        onClick={handleChangePin}
                        className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                      >
                        Change PIN
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-amber-700 mt-2 font-medium">
                    🔒 You'll need to enter your 6-digit transfer PIN to complete this transfer.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                <motion.button
                  onClick={() => {
                    console.log('Button clicked - pinRequired:', pinRequired, 'pinStatus:', pinStatus);
                    if (pinRequired) {
                      console.log('Opening PIN modal for validation');
                      setPinModalMode('validate');
                      setPinModalOpen(true);
                    } else {
                      console.log('Submitting transfer directly');
                      handleSubmitTransfer();
                    }
                  }}
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  className={`w-full py-4 rounded-xl font-semibold text-lg transition-colors ${
                    loading
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : pinRequired
                      ? 'bg-amber-600 text-white hover:bg-amber-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </div>
                  ) : (
                    pinRequired ? (
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <span>Enter PIN to Confirm</span>
                      </div>
                    ) : (
                      'Confirm Transfer'
                    )
                  )}
                </motion.button>

                <button
                  onClick={() => setStep(2)}
                  disabled={loading}
                  className="w-full py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Back to Edit
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Transfer PIN Modal */}
      <TransferPinModal
        isOpen={pinModalOpen}
        onClose={handlePinModalClose}
        onSuccess={handlePinSuccess}
        mode={pinModalMode}
        title={
          pinModalMode === 'validate' ? 'Confirm Transfer with PIN' :
          pinModalMode === 'set' ? 'Set Transfer PIN' :
          pinModalMode === 'change' ? 'Change Transfer PIN' :
          'Transfer PIN'
        }
        description={
          pinModalMode === 'validate' ? 'Enter your 6-digit transfer PIN to complete this transfer.' :
          pinModalMode === 'set' ? 'Create a secure 6-digit PIN for your transfers. This PIN will be required for all future transfers.' :
          pinModalMode === 'change' ? 'Enter your current PIN and create a new one.' :
          'Transfer PIN Required'
        }
      />
    </div>
  );
};

export default Transfer; 